export interface TabNotificationConfig {
  enabled: boolean
  showInTitle: boolean
  showInFavicon: boolean
  soundEnabled: boolean
  maxNotifications: number
}

export interface PendingNotification {
  id: string
  title: string
  message: string
  timestamp: number
  source: 'websocket' | 'local'
  priority: 'low' | 'medium' | 'high'
  read: boolean
}

export class TabNotificationService {
  private static instance: TabNotificationService
  private pendingNotifications: PendingNotification[] = []
  private originalTitle = ''
  private originalFavicon = ''
  private titleInterval: NodeJS.Timeout | null = null
  private config: TabNotificationConfig = {
    enabled: true,
    showInTitle: true,
    showInFavicon: true,
    soundEnabled: true,
    maxNotifications: 10,
  }
  private isTabVisible = true
  private externalBadgeCount = 0

  private constructor() {
    this.initializeOriginalValues()
  }

  public static getInstance(): TabNotificationService {
    if (!TabNotificationService.instance) {
      TabNotificationService.instance = new TabNotificationService()
    }
    return TabNotificationService.instance
  }

  private initializeOriginalValues() {
    if (typeof document !== 'undefined') {
      this.originalTitle = document.title
      this.originalFavicon = this.getCurrentFaviconHref()
    }
  }

  private getCurrentFaviconHref() {
    if (typeof document === 'undefined') {
      return '/favicon/favicon-32x32.png'
    }

    const sizedPngFavicon = document.querySelector<HTMLLinkElement>('link[rel="icon"][sizes="32x32"]')
    if (sizedPngFavicon?.href) {
      return sizedPngFavicon.href
    }

    const anyFavicon = document.querySelector<HTMLLinkElement>('link[rel*="icon"]')
    return anyFavicon?.href || '/favicon/favicon-32x32.png'
  }

  public setConfig(config: Partial<TabNotificationConfig>) {
    this.config = { ...this.config, ...config }
  }

  public getConfig(): TabNotificationConfig {
    return { ...this.config }
  }

  public isCurrentTabVisible(): boolean {
    return this.isTabVisible
  }

  public setTabVisibility(isVisible: boolean) {
    const wasHidden = !this.isTabVisible
    this.isTabVisible = isVisible

    if (isVisible && wasHidden) {
      this.updatePageIndicators()
    } else if (!isVisible) {
      this.updatePageIndicators()
    }
  }

  public addNotification(notification: Omit<PendingNotification, 'id' | 'timestamp' | 'read'>) {
    if (!this.config.enabled) {
      return
    }

    const newNotification: PendingNotification = {
      ...notification,
      id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      read: false,
    }

    this.pendingNotifications.push(newNotification)

    if (this.pendingNotifications.length > this.config.maxNotifications) {
      this.pendingNotifications = this.pendingNotifications.slice(-this.config.maxNotifications)
    }

    // Always update indicators regardless of visibility
    // This way indicators show when user returns to tab
    this.updatePageIndicators()

    // Play sound for any notification if sound is enabled
    if (this.config.soundEnabled) {
      this.playNotificationSound()
    }
  }

  public markNotificationAsRead(id: string) {
    const notification = this.pendingNotifications.find(n => n.id === id)
    if (notification) {
      notification.read = true
      this.updatePageIndicators()
    }
  }

  public clearAllNotifications() {
    this.pendingNotifications = []
    this.clearPageIndicators()
  }

  public clearNotificationsOnUserInteraction() {
    // Clear notifications when user explicitly interacts with them
    // This is different from automatic clearing on tab visibility
    this.clearAllNotifications()
  }

  public getUnreadCount(): number {
    return this.pendingNotifications.filter(n => !n.read).length
  }

  public getAllNotifications(): PendingNotification[] {
    return [...this.pendingNotifications]
  }

  public getUnreadNotifications(): PendingNotification[] {
    return this.pendingNotifications.filter(n => !n.read)
  }

  private updatePageIndicators() {
    const totalCount = this.getUnreadCount() + this.externalBadgeCount

    if (totalCount > 0) {
      if (this.config.showInTitle) {
        this.updateTitle(totalCount)
      }

      if (this.config.showInFavicon && this.externalBadgeCount > 0) {
        this.updateFavicon(true, totalCount)
        // this.playNotificationSound()
      }
    } else {
      this.clearPageIndicators()
    }
  }

  private updateTitle(count: number) {
    if (typeof document === 'undefined') return

    // Stop any existing title animation
    if (this.titleInterval) {
      clearInterval(this.titleInterval)
      this.titleInterval = null
    }

    const notificationTitle = `(${count}) ${this.originalTitle}`
    document.title = notificationTitle
  }

  private updateFavicon(hasNotifications: boolean, count = 0) {
    if (typeof document === 'undefined') return

    const existingFavicons = document.querySelectorAll('link[rel*="icon"]')
    existingFavicons.forEach(favicon => {
      if (favicon.parentNode) {
        favicon.parentNode.removeChild(favicon)
      }
    })

    const faviconElement = document.createElement('link')
    faviconElement.rel = 'icon'
    faviconElement.type = 'image/png'
    document.head.appendChild(faviconElement)

    if (hasNotifications) {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) {
        // eslint-disable-next-line no-console
        console.warn('Canvas context not available, using simple red badge with number')
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.createSimpleFaviconWithDot(context!, faviconElement, count)
        return
      }

      canvas.width = 32
      canvas.height = 32

      const img = new Image()
      img.onload = () => {
        try {
          context.clearRect(0, 0, 32, 32)
          context.drawImage(img, 0, 0, 32, 32)

          const offset = 10 // Increased offset to ensure the larger dot fits
          const badgeRadius = count >= 9 ? 15 : 10 // Bigger red dot
          let badgeX = 32 - offset
          let badgeY = 32 - offset

          // Ensure the red dot fits within the favicon bounds
          if (badgeX - badgeRadius < 0) {
            badgeX = badgeRadius
          }
          if (badgeY - badgeRadius < 0) {
            badgeY = badgeRadius
          }

          context.fillStyle = '#dc2626'
          context.beginPath()
          context.arc(badgeX, badgeY, badgeRadius, 0, 2 * Math.PI)
          context.fill()

          const displayCount = count >= 9 ? '9+' : count.toString()
          const fontSize = count >= 9 ? '16px' : '22px' // Adjusted font size for larger dot
          context.fillStyle = '#ffffff'
          context.font = `bold ${fontSize} Arial`
          context.textAlign = 'center'
          context.textBaseline = 'middle'

          context.shadowColor = 'rgba(0,0,0,0.5)'
          context.shadowBlur = 1
          context.shadowOffsetX = 0.5
          context.shadowOffsetY = 0.5

          context.fillText(displayCount, badgeX, badgeY)

          context.shadowColor = 'transparent'
          context.shadowBlur = 0
          context.shadowOffsetX = 0
          context.shadowOffsetY = 0

          const dataUrl = canvas.toDataURL('image/png')
          faviconElement.href = dataUrl
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Could not add red dot overlay:', error)
          this.createSimpleFaviconWithDot(context, faviconElement, count)
        }
      }

      img.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn('Could not load favicon image, creating simple icon with dot')
        this.createSimpleFaviconWithDot(context, faviconElement, count)
      }

      img.src = this.originalFavicon
    } else {
      // Restore original favicon without any modifications
      faviconElement.href = this.originalFavicon
    }
  }

  private createSimpleFaviconWithDot(context: CanvasRenderingContext2D, faviconElement: HTMLLinkElement, count = 0) {
    const canvas = context.canvas
    canvas.width = 32
    canvas.height = 32

    context.clearRect(0, 0, 32, 32)
    context.fillStyle = '#3b82f6'
    context.fillRect(4, 4, 24, 24)

    const offset = 6
    const badgeRadius = count > 9 ? 15 : 13

    // Ensure the red dot fits within the favicon bounds
    let badgeX = 32 - offset
    let badgeY = 32 - offset

    if (badgeX - badgeRadius < 0) {
      badgeX = badgeRadius
    }
    if (badgeY - badgeRadius < 0) {
      badgeY = badgeRadius
    }

    context.fillStyle = '#ef4444'
    context.beginPath()
    context.arc(badgeX, badgeY, badgeRadius, 0, 2 * Math.PI)
    context.fill()

    const displayCount = count > 99 ? '99+' : count.toString()
    const fontSize = count > 9 ? '9px' : '12px' // Adjusted font size for larger dot
    context.fillStyle = '#ffffff'
    context.font = `bold ${fontSize} Arial`
    context.textAlign = 'center'
    context.textBaseline = 'middle'

    context.shadowColor = 'rgba(0,0,0,0.5)'
    context.shadowBlur = 1
    context.shadowOffsetX = 0.1
    context.shadowOffsetY = 0.2

    context.fillText(displayCount, badgeX, badgeY)

    context.shadowColor = 'transparent'
    context.shadowBlur = 0
    context.shadowOffsetX = 0
    context.shadowOffsetY = 0

    const dataUrl = canvas.toDataURL('image/png')
    faviconElement.href = dataUrl
  }

  private clearPageIndicators() {
    if (typeof document === 'undefined') return

    if (this.titleInterval) {
      clearInterval(this.titleInterval)
      this.titleInterval = null
    }

    const totalCount = this.getUnreadCount()
    document.title = totalCount > 0 ? `(${totalCount}) ${this.originalTitle}` : this.originalTitle

    const existingFavicons = document.querySelectorAll('link[rel*="icon"]')
    existingFavicons.forEach(favicon => {
      if (favicon.parentNode) {
        favicon.parentNode.removeChild(favicon)
      }
    })

    const faviconElement = document.createElement('link')
    faviconElement.rel = 'icon'
    faviconElement.type = 'image/png'
    faviconElement.href = this.originalFavicon
    document.head.appendChild(faviconElement)
  }

  private clearTitleOnly() {
    if (typeof document === 'undefined') return

    if (this.titleInterval) {
      clearInterval(this.titleInterval)
      this.titleInterval = null
    }

    const totalCount = this.getUnreadCount()
    document.title = totalCount > 0 ? `(${totalCount}) ${this.originalTitle}` : this.originalTitle
  }

  private playNotificationSound() {
    if (typeof window === 'undefined' || !window.AudioContext) return

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

      // Create a more pleasant notification sound with two tones
      this.playTone(audioContext, 800, 0, 0.15) // First tone
      this.playTone(audioContext, 1000, 0.2, 0.15) // Second tone (higher pitch)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Could not play notification sound:', error)
    }
  }

  private playTone(audioContext: AudioContext, frequency: number, startTime: number, duration: number) {
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)

    // Set frequency
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime + startTime)

    // Create a pleasant envelope (fade in and out)
    gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime)
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + startTime + 0.05) // Quick fade in
    gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + startTime + duration - 0.05) // Sustain
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + startTime + duration) // Fade out

    // Use a softer wave type
    oscillator.type = 'sine'

    oscillator.start(audioContext.currentTime + startTime)
    oscillator.stop(audioContext.currentTime + startTime + duration)
  }

  public destroy() {
    this.clearPageIndicators()
    this.pendingNotifications = []
  }

  public debugFavicon() {
    if (typeof document === 'undefined') return

    const faviconElements = document.querySelectorAll('link[rel*="icon"]')
    const info = {
      faviconCount: faviconElements.length,
      favicons: Array.from(faviconElements).map(el => ({
        rel: el.getAttribute('rel'),
        href: el.getAttribute('href'),
        type: el.getAttribute('type'),
      })),
      originalFavicon: this.originalFavicon,
      hasNotifications: this.getUnreadCount() > 0,
      isTabVisible: this.isTabVisible,
    }

    // eslint-disable-next-line no-console
    console.log('Favicon Debug Info:', info)
    return info
  }

  /**
   * Directly update the title and favicon with a provided unread count
   * (e.g., from chat total unread). Does not mutate internal notification list.
   */
  public updateChatUnreadBadge(count: number) {
    if (!this.config.enabled) return
    this.externalBadgeCount = Math.max(0, count)
    this.updatePageIndicators()
  }

  public clearChatUnreadBadge() {
    this.externalBadgeCount = 0
    this.updatePageIndicators()
  }
}

export const tabNotificationService = TabNotificationService.getInstance()

export default tabNotificationService
